<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import {
  isIntegrationSummary,
  isMeResponse,
  isVoxServiceAccountInput,
  type IntegrationSummary,
} from '@calloneer/contracts';
import { apiRequest } from '../api.js';
import { useAudioStore } from '../stores/audio.js';
const audio = useAudioStore();
const form = reactive({ ...audio.preferences });
const integration = ref<IntegrationSummary>({
  status: 'NOT_CONFIGURED',
  maskedAccount: null,
  updatedAt: null,
  message: null,
});
const accountName = ref('');
const node = ref('NODE_1');
const credential = ref<unknown>(null);
const message = ref('');
const microphones = ref<readonly MediaDeviceInfo[]>([]);
const speakers = ref<readonly MediaDeviceInfo[]>([]);
const canSelectOutput = ref(typeof HTMLMediaElement.prototype.setSinkId === 'function');
let integrationPollTimer: number | undefined;
let disposed = false;
let settingsDeviceChangeHandler: (() => void) | undefined;
const stopIntegrationPolling = (): void => {
  if (integrationPollTimer === undefined) return;
  window.clearTimeout(integrationPollTimer);
  integrationPollTimer = undefined;
};
const scheduleIntegrationPolling = (): void => {
  stopIntegrationPolling();
  if (disposed || integration.value.status !== 'PROVISIONING') return;
  integrationPollTimer = window.setTimeout(() => {
    integrationPollTimer = undefined;
    void refreshIntegration();
  }, 2_000);
};
const refreshIntegration = async (): Promise<void> => {
  try {
    integration.value = await apiRequest({
      path: '/v1/me/integration',
      guard: isIntegrationSummary,
    });
    scheduleIntegrationPolling();
  } catch {
    if (!disposed) message.value = 'Could not refresh Voximplant setup status.';
  }
};
const enumerateAudioDevices = async (): Promise<void> => {
  if (!navigator.mediaDevices?.enumerateDevices) return;
  const devices = await navigator.mediaDevices.enumerateDevices();
  microphones.value = devices.filter((device) => device.kind === 'audioinput');
  speakers.value = devices.filter((device) => device.kind === 'audiooutput');
  let preferencesChanged = false;
  if (form.inputId && !microphones.value.some((device) => device.deviceId === form.inputId)) {
    form.inputId = null;
    preferencesChanged = true;
  }
  if (form.outputId && !speakers.value.some((device) => device.deviceId === form.outputId)) {
    form.outputId = null;
    preferencesChanged = true;
  }
  if (preferencesChanged) audio.save({ ...audio.preferences, ...form });
};
const requestAudioAccess = async (): Promise<void> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach((track) => track.stop());
    await enumerateAudioDevices();
    message.value = 'Audio devices refreshed.';
  } catch {
    message.value = 'Microphone permission was not granted.';
  }
};
onMounted(async () => {
  integration.value = (await apiRequest({ path: '/v1/me', guard: isMeResponse })).integration;
  scheduleIntegrationPolling();
  await enumerateAudioDevices();
  settingsDeviceChangeHandler = () => void enumerateAudioDevices();
  navigator.mediaDevices?.addEventListener('devicechange', settingsDeviceChangeHandler);
});
onBeforeUnmount(() => {
  disposed = true;
  stopIntegrationPolling();
  if (settingsDeviceChangeHandler)
    navigator.mediaDevices?.removeEventListener('devicechange', settingsDeviceChangeHandler);
});
const readCredential = async (event: Event): Promise<void> => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    const parsed: unknown = JSON.parse(await file.text());
    if (!isVoxServiceAccountInput(parsed)) throw new Error();
    credential.value = parsed;
    message.value = 'Credential file loaded. It will be encrypted and never shown again.';
  } catch {
    credential.value = null;
    message.value =
      'That file is not a Voximplant service-account credential. Upload the JSON downloaded with Generate key.';
  }
};
const connect = async (): Promise<void> => {
  integration.value = await apiRequest({
    path: '/v1/me/integration',
    method: 'PUT',
    body: { serviceAccount: credential.value, accountName: accountName.value, node: node.value },
    guard: isIntegrationSummary,
  });
  credential.value = null;
  scheduleIntegrationPolling();
};
const saveAudio = (): void => {
  audio.save({ ...form });
  message.value = 'Audio preferences saved in this browser.';
};
</script>

<template>
  <div class="settings-grid">
    <section class="panel">
      <p class="eyebrow">WRITE-ONLY CREDENTIALS</p>
      <h1>Voximplant</h1>
      <div class="integration-state">
        <span class="dot" :class="integration.status.toLowerCase()"></span>
        <div>
          <strong>{{ integration.status.replace('_', ' ') }}</strong>
          <p>{{ integration.maskedAccount ?? 'No account connected' }}</p>
        </div>
      </div>
      <p v-if="integration.message" class="notice">{{ integration.message }}</p>
      <label
        >Account name<input
          v-model="accountName"
          placeholder="your Voximplant account name" /></label
      ><label
        >Connection node<select v-model="node">
          <option v-for="n in 13" :key="n" :value="`NODE_${n}`">NODE_{{ n }}</option>
        </select>
        <small class="field-help"
          >Use the node shown in Voximplant Control Panel → Credentials for working with API, SDK,
          SIP.</small
        ></label
      ><label
        >Service-account JSON (not an API key)<input
          type="file"
          accept="application/json"
          @change="readCredential"
      /></label>
      <p class="field-help">
        Create this file in Voximplant Control Panel → Settings → Service Accounts → Add → assign a
        role → Generate key. The downloaded JSON is encrypted after upload and never shown again.
      </p>
      <button class="primary" :disabled="!credential || !accountName" @click="connect">
        Validate and connect
      </button>
    </section>
    <section class="panel">
      <p class="eyebrow">THIS BROWSER ONLY</p>
      <h1>Audio</h1>
      <label
        >Microphone<select v-model="form.inputId">
          <option :value="null">System default</option>
          <option v-for="device in microphones" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Microphone' }}
          </option>
        </select></label
      ><label
        >Speaker<select v-model="form.outputId" :disabled="!canSelectOutput">
          <option :value="null">System default</option>
          <option v-for="device in speakers" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Speaker' }}
          </option>
        </select></label
      >
      <p v-if="!canSelectOutput" class="notice">
        This browser does not support selecting a separate speaker.
      </p>
      <button class="secondary" @click="requestAudioAccess">Allow and refresh devices</button>
      <label class="toggle"
        ><input v-model="form.echoCancellation" type="checkbox" /> Echo cancellation</label
      ><label class="toggle"
        ><input v-model="form.noiseSuppression" type="checkbox" /> Noise suppression</label
      ><label class="toggle"
        ><input v-model="form.autoGainControl" type="checkbox" /> Automatic gain control</label
      ><button class="secondary" @click="saveAudio">Save audio settings</button>
      <p class="fine-print">
        Microphone and speaker IDs are machine-specific and never sent to Calloneer.
      </p>
    </section>
    <p v-if="message" class="toast">{{ message }}</p>
  </div>
</template>
